import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';
import { CSSTransition } from 'react-transition-group';
import { VARIATION } from '../../../propTypes';
import { getLayerProps, LAYER_TYPES } from '../../../lib';
import { VARIATION_DOM_ID_ATTRIBUTE } from '../../cards/LogoProjectCard/constants';
import Watermark from '../../Watermark';
import { VARIATIONS_DISPLAY_MODES } from '../../../constants';
import { SVGRootByType, getRootsVerticalChanges } from './lib';
import { useVariationResourceLoad } from './hooks/useVariationResourceLoad';
import { useVariationMutationObserver } from './hooks/useVariationMutationObserver';
import { useVariationViewBox } from './hooks/useVariationViewBox';
import { useVariationRoots } from './hooks/useVariationRoots';
import {
    VARIATION_SVG_ATTRIBUTES,
    LOADING_BACKGROUND_COLOR,
    DEFAULT_VIEWBOX_CONFIG
} from './constants';
import './style.scss';

/**
 * Returns variation SVG wrapper attributes
 * @param {String} viewBox the calculate variation view box
 */
const getVariationSvgAttributes = ({ viewBox }) => ({
    viewBox,
    ...VARIATION_SVG_ATTRIBUTES
});

/**
 * Returns proper variation background according to is ready / actual background.
 * @param {String} backgroundColor
 * @param {Boolean} isReady
 * @returns {String}
 */
const getBackgroundColor = (backgroundColor = 'transparent', isReady) => (
    isReady ? backgroundColor : (backgroundColor || LOADING_BACKGROUND_COLOR)
);

const Variation = ({
    id,
    roots,
    size,
    scale = 1,
    backgroundColor,
    previewUrl,
    mode = VARIATIONS_DISPLAY_MODES.SVG,
    viewBoxConfig = DEFAULT_VIEWBOX_CONFIG,
    variationCentered = false,
    applyVerticalChanges = false,
    withWatermark = false,
    withTransparentPattern = false,
    rootsVisible = true,
    observeChanges = false,
    onPreviewLoad = noop,
    dataVariationId,
    onClick = noop,
    onTextFieldsRenderTick = noop,
    classNames = ''
}) => {
    const [tickRenderedFields, setTickRenderedFields] = useState([]);
    const isPreview = mode === VARIATIONS_DISPLAY_MODES.PREVIEW;

    const [variationRoots, handleRootUpdates] = useVariationRoots(id, roots);
    const [loadVariation, isReady, previewLoadingTime] = useVariationResourceLoad({ id, roots: variationRoots, previewUrl }, isPreview);
    const [updateVariation] = useVariationMutationObserver({ id, roots: variationRoots }, observeChanges);

    const [viewBox] = useVariationViewBox(id, variationRoots, size, viewBoxConfig);

    const onUpdate = (update) => {
        const verticalUpdates = applyVerticalChanges ? getRootsVerticalChanges(update, variationRoots, roots) : [];
        const rootUpdates = [update, ...verticalUpdates];

        rootUpdates.forEach((update) => {
            observeChanges && updateVariation(update);
        });

        handleRootUpdates(rootUpdates);
    };

    const onTextFieldRenderTick = (layerRole) => {
        tickRenderedFields.push(layerRole);

        const textRoles = variationRoots
            .filter(({ layerType }) => layerType === LAYER_TYPES.TEXT)
            .map((layer) => layer.layerRole);

        const finishedTick = textRoles.every((role) => tickRenderedFields.includes(role));

        if (finishedTick) {
            setTickRenderedFields([]);
            onTextFieldsRenderTick(id);
        }
    };

    useEffect(() => {
        !isReady && previewUrl && loadVariation();
    }, [isReady, previewUrl, loadVariation]);

    useEffect(() => {
        const rootUpdates = roots.map((root) => ({
            root: {
                layerId: root.layerId,
                x: root.x,
                y: root.y,
                width: root.width,
                height: root.height,
                viewBox: root.viewBox,
                style: root.style
            }
        }));
        handleRootUpdates(rootUpdates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variationCentered]);

    useEffect(() => {
        if (previewLoadingTime) {
            onPreviewLoad({
                loadTime: previewLoadingTime
            });
        }
    }, [previewLoadingTime, onPreviewLoad]);

    const renderRoots = () => variationRoots.map(({ layerType, layerId, ...root }) => {
        const isIconRoot = layerType === LAYER_TYPES.ICON;
        if (isPreview && isIconRoot) {
            return <image key={layerId} {...getLayerProps(root)} href={previewUrl} xlinkHref={previewUrl}/>;
        }

        const RootComponent = SVGRootByType(layerType);
        return (
            <RootComponent key={layerId}
                onUpdate={onUpdate}
                onRenderTick={onTextFieldRenderTick}
                layerId={layerId}
                data-layer-type={layerType}
                {...root}/>
        );
    });

    const variationBackground = getBackgroundColor(backgroundColor, isReady);

    return (
        <div onClick={onClick}
            className={classnames('variation-wrapper', classNames)}
            {...{ [VARIATION_DOM_ID_ATTRIBUTE]: dataVariationId }}>
            {backgroundColor && withTransparentPattern && (
                <CSSTransition in={isReady && rootsVisible}
                    timeout={300}
                    classNames="fade"
                    unmountOnExit>
                    <div className="variation-transparent-pattern"/>
                </CSSTransition>
            )}
            <div className="variation-background" style={{ backgroundColor: variationBackground }}/>
            {withWatermark && <Watermark backgroundColor={variationBackground}/>}
            <CSSTransition in={isReady && rootsVisible}
                timeout={300}
                classNames="fade"
                unmountOnExit>
                <svg className="variation-svg" style={{ transform: `scale(${scale})` }} {...getVariationSvgAttributes({ viewBox })}>
                    {renderRoots()}
                </svg>
            </CSSTransition>
        </div>
    );
};

Variation.propTypes = VARIATION;

export default Variation;
